<template>
    <div>
        <BModal 
            v-model="modalShow" 
            v-bind:title="$t('activateFor') + user.name + ' (robot)'" 
            size="xl"
            @ok="handleOk"
        >
            <div class="k4-modal__body container-fluid">
                <div class="row pb-2">
                    <div class="col-lg-4">{{ $t("name") }}</div>
                    <div class="col-lg-8"><input type="text" class="form-control" v-model="user.name" disabled></div>
                </div>
                <div class="row pb-2">
                    <div class="col-lg-4">{{ $t("pinCode") }}</div>
                    <div class="col-lg-8"><input type="text" class="form-control" v-model="user.pin" disabled></div>
                </div>
                <div class="row pb-2">
                    <div class="col-lg-4">{{ $t("dayOfWork") }}</div>
                    <div class="col-lg-8">
                        <VueDatePicker v-model="dateOfWork" :disabled="this.isReadOnly" :clearable="false" :enable-time-picker="false" auto-apply format="dd/MM/yyyy" />
                    </div>
                </div>
                <!-- Robot Schedule Timeline -->
                <div class="row pb-2">
                    <div class="col-12">
                        <div v-if="isLoadingCheckpoints" class="text-center py-5">
                            <div class="spinner-border text-primary" role="status">
                                <span class="sr-only">{{ $t('loading') }}...</span>
                            </div>
                            <p class="mt-2">{{ $t('loading') }} checkpoints...</p>
                        </div>
                        <RobotScheduleTimeline
                            v-else-if="!isLoadingCheckpoints"
                            :key="`${user.id}-${timelineKey}`"
                            :value="selectedCheckpoints"
                            :available-checkpoints="checkpoints"
                            :date-of-work="dateOfWork"
                            :evt="evt"
                            :is-read-only="isReadOnly"
                            @validation-error="handleValidationError"
                            @edit-checkpoint="handleEditCheckpoint"
                            @input="handleCheckpointsUpdate"
                        />
                    </div>
                </div>
            </div>

            <template v-slot:modal-footer>
                <div v-if="this.isReadOnly">
                    <button class="btn btn-outline-secondary" @click="modalShow = !modalShow">{{ $t("cancel")}}</button>
                </div>
                <div v-else class="d-flex align-items-center justify-content-between">
                    <ButtonExportPDF ref="ButtonExportPDF" @click.native="exportPDF"></ButtonExportPDF>
                    <div>
                        <button class="btn btn-outline-secondary mr-2" @click="modalShow = !modalShow">{{ $t("cancel")}}</button>
                        <button class="btn btn-danger mr-2" @click="askToDelete" >{{ $t("delete")}}</button>
                    </div>
                </div>
            </template>
        </BModal>

        <BModal v-model="confirmModal.show" v-bind:title="$t('deleteUser')">
            <span v-if="user">{{ $t("comfirmDelete") }} {{user.name}} ?</span>

            <template #modal-footer>
                <div class="w-100">
                    <button type="button" @click="confirmModal.show = false" class="btn btn-outline-secondary mx-1">{{$t("cancel")}}</button>
                    <button type="button" @click="deleteUser" class="btn btn-danger mx-1">{{$t('delete')}}</button>
                </div>
            </template>
        </BModal>
        <GenerateRobotPDF ref="robotPdf" @onEndExportPDF = "onEndExportPDF" />
    </div>
</template>
<script>
    import CheckpointService from '../../services/checkpoint.service';
    import UserService from '../../services/user.service';
    import utilMixin from '../../mixins/utils.mixins';
    import RobotCheckpointModel from '../../models/robotCheckpointModel';
    import GenerateRobotPDF from '../pdf/GenerateRobotPDF.vue';
    import ButtonExportPDF from '../pdf/ButtonExportPDF.vue';
    import RobotScheduleTimeline from './RobotScheduleTimeline.vue';
    import { DateTime } from "luxon";
    import { mapGetters } from 'vuex';

    export default {
        name:"UpdatePinRobot",
        mixins: [utilMixin],
        components: {
            GenerateRobotPDF,
            ButtonExportPDF,
            RobotScheduleTimeline
        },
        props : ['evt', "isReadOnly"],
        data: function(){
            return {
                modalShow:false,
                user:{},
                dateOfWork: new Date(),
                selectedCheckpoints:[],
                isLoadingCheckpoints: false,
                timelineKey: 0,
                confirmModal:{
                    show:false,
                },
                selectedValue: new Date()
            }
        },
        computed: {
            ...mapGetters({
                checkpoints: 'getCheckpoints'
            })
        },
        watch:{
            user: function(val){
                if(val && val.eventId){
                    this.selectedCheckpoints=[];
                    this.getRobotCheckpoint();
                }
            },
            modalShow: function(val){
                if(val == false){
                    this.user={};
                    this.selectedCheckpoints=[];
                }
            },
            dateOfWork(newValue) {
                // Set the all start and end dates to match the selected date of work
                this.selectedCheckpoints.forEach(checkpoint => 
                    {
                        const [dateOfWorkYear, dateOfWorkMonth, dateOfWorkDay] = newValue.split("-").map(Number);
                        checkpoint.startDate = DateTime.fromObject({
                            year: dateOfWorkYear,
                            month: dateOfWorkMonth,
                            day: dateOfWorkDay,
                            hour: checkpoint.startDate.hour,
                            minute: checkpoint.startDate.minute
                        }, { zone: this.evt.timezoneIso });

                        checkpoint.endDate = DateTime.fromObject({
                            year: dateOfWorkYear,
                            month: dateOfWorkMonth,
                            day: dateOfWorkDay,
                            hour: checkpoint.endDate.hour,
                            minute: checkpoint.endDate.minute,
                        }, { zone: this.evt.timezoneIso });
                    }
                );
            },
        },
        methods:{
            onEndExportPDF(){
                this.$refs.ButtonExportPDF.onEndExportPDF();
            },
            handleOk(bvModalEvent) {
                if (!this.isReadOnly) {
                    bvModalEvent.preventDefault();
                    this.updateUserCheckpoint();
                }
            },
            handleCheckpointsUpdate(checkpoints) {
                this.selectedCheckpoints = checkpoints;
            },
            getTimeFromLuxonDateTime(dateTime){
                return dateTime.toFormat("HH:mm 'UTC'ZZ");
            },
            checkpointName: function(id){
                if(this.checkpoints && id !== undefined){return this.checkpoints.find((i) => i.id == id).name;}
                return '';
            },
            getRobotCheckpoint: function(){
                if(this.user && this.user.eventId){
                    this.isLoadingCheckpoints = true;
                    CheckpointService.getRobotCheckpoint(this.user).then(
                        resp => {
                            var response = resp.status ? resp : resp.response;
                            if(response.status == 200){
                                // clear selected checkpoints
                                this.selectedCheckpoints=[];
                                this.dateOfWork = DateTime.now().toFormat("yyyy-MM-dd");
                                if (response.data.length != 0) {
                                    const checkpoints = [];
                                    // startDateTimeLocal and endDateTimeLocal are given in local time and use the following format 2023-06-06T00:30:00
                                    response.data.forEach(i => checkpoints.push(
                                        // Rebuild selected checkpoints to convert DateTime to Luxon objects
                                        new RobotCheckpointModel(i.checkpointId, i.direction, DateTime.fromISO(i.startDateTimeLocal, { setZone: true }), DateTime.fromISO(i.endDateTimeLocal, { setZone: true }))
                                    ));
                                    // update date of work with first selected checkpoint
                                    this.dateOfWork = checkpoints[0].startDate.toFormat('yyyy-MM-dd');
                                    
                                    // Assigner les checkpoints AVANT de désactiver le loading
                                    this.selectedCheckpoints = [...checkpoints];
                                    
                                    // Attendre deux cycles de rendu pour s'assurer que la réactivité est complète
                                    this.$nextTick(() => {
                                        this.$nextTick(() => {
                                            this.isLoadingCheckpoints = false;
                                        });
                                    });
                                } else {
                                    // Pas de checkpoints - désactiver le loading immédiatement
                                    this.isLoadingCheckpoints = false;
                                }
                            }
                            else {
                                this.showToast(this.$t('error'), response.data, 'danger');
                                this.isLoadingCheckpoints = false;
                            }
                        }
                    ).catch(error => {
                        this.showToast(this.$t('error'), error, 'danger');
                        this.isLoadingCheckpoints = false;
                    });
                }
            },
            showModal: function(){
                this.modalShow=true;
                this.timelineKey++; // Forcer la recréation du composant timeline
                // Charger les checkpoints quand on ouvre la modal
                if(this.user && this.user.eventId){
                    this.getRobotCheckpoint();
                }
            },
            updateUserCheckpoint : function(){
                var userCheckpoints = [];

                // Convert into RobotUserCheckpointModel backend DTO
                this.selectedCheckpoints.forEach(checkpoint => {
                    userCheckpoints.push(
                        {
                            userId: this.user.id, 
                            checkpointId: checkpoint.checkpointId, 
                            direction: checkpoint.direction, 
                            startDateTimeLocal: checkpoint.startDate.toISO(),
                            endDateTimeLocal: checkpoint.endDate.toISO()
                        }
                    );
                });

                CheckpointService.putRobotCheckpoints(userCheckpoints, this.user.eventId).then(
                    resp => {
                        var response = resp.status ? resp : resp.response;
                        if(response.status == 200) {
                            this.showToast(this.$t('success'), response.data, 'success')
                            this.modalShow=false;
                            this.$emit("action_ok");
                        } else {
                            this.showToast(this.$t('error'), response.data, 'danger');
                        }
                    }
                ).catch(error => {
                    this.showToast(this.$t('error'), error, 'danger');
                });
            },
            askToDelete:function(){
                this.confirmModal.show = true;
            },
            deleteUser: function(){
                if (this.user){
                    UserService.deleteUser(this.user).then(
                        resp => {
                            var response = resp.status ? resp : resp.response; 
                            if(response.status == 200){
                                this.showToast(this.$t('confirmedDeleteUser'), this.user.name + this.$t('hasBeenDeleted'));
                                this.$emit("delete_ok");
                                this.modalShow = false;
                                this.confirmModal.show = false;
                            }
                            else {
                                this.showToast(this.$t('error'), response.data, 'danger');
                            }
                        }
                    )
                }
            },
            exportPDF: function(){
                this.$refs.ButtonExportPDF.onStartExportPDF();

                this.$refs.robotPdf.users.push(this.user);
                this.$refs.robotPdf.checkpoints = this.checkpoints;
                this.$refs.robotPdf.createPDF();
            },
            
            handleValidationError: function(message) {
                this.showToast(this.$t('warning'), message, 'warning');
            },
            
            handleEditCheckpoint: function(data) {
                // TODO: Implement modal dialog for editing checkpoint
                // For now, we can show an alert with checkpoint details
                const checkpoint = data.checkpoint;
                const checkpointName = this.checkpointName(checkpoint.checkpointId);
                const startTime = this.getTimeFromLuxonDateTime(checkpoint.startDate);
                const endTime = this.getTimeFromLuxonDateTime(checkpoint.endDate);
                
                alert(`Éditer: ${checkpointName} (${checkpoint.direction}) - ${startTime} à ${endTime}`);
            }
        }
    }
</script>

<style scoped>
    .k4-modal__body {
        min-height: 50vh;
    }
    .k4-removeLast {
        color: var(--red);
        background-color: var(--white);
        border-radius: 50%;
    }
    .k4-removeLast:hover {
        color: var(--danger);
        box-shadow: rgba(0, 0, 0, 0.3) 0px 19px 38px, rgba(0, 0, 0, 0.22) 0px 15px 12px;
    }
    .k4-selectCheckpoint, .k4-selectDirection, .k4-startTimePicker, .k4-endTimePicker {
        box-shadow: rgba(0, 0, 0, 0.12) 0px 1px 3px, rgba(0, 0, 0, 0.24) 0px 1px 2px;
    }
</style>
